import React, { useState } from 'react';
import { CheckCircle, FileText, Download, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';

const AssessmentComplete = ({ onMedicalConsultation, onBack, emergencyData }) => {
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [generatingWord, setGeneratingWord] = useState(false);

  const handleGeneratePDF = async () => {
    setGeneratingPDF(true);
    try {
      // Debug: Log the data being sent
      console.log('Emergency Data being sent to PDF generation:', emergencyData);
      
      // Call backend API to generate PDF report
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emergency/report/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emergencyData)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Emergency_Assessment_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('PDF report downloaded successfully!');
      } else {
        const errorText = await response.text();
        console.error('PDF generation error:', errorText);
        alert('Error generating PDF report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF report. Please check your connection and try again.');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const handleGenerateWord = async () => {
    setGeneratingWord(true);
    try {
      // Call backend API to generate Word report
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/emergency/report/docx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emergencyData)
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Emergency_Assessment_${new Date().toISOString().split('T')[0]}.docx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('Word report downloaded successfully!');
      } else {
        const errorText = await response.text();
        console.error('Word generation error:', errorText);
        alert('Error generating Word report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating Word:', error);
      alert('Error generating Word report. Please check your connection and try again.');
    } finally {
      setGeneratingWord(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-black py-4">
      <div className="w-full max-w-sm mx-auto px-4">
        <div className="flex flex-col min-h-screen bg-zinc-900 rounded-3xl border-2 border-orange-500/30 shadow-2xl overflow-hidden p-6">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-24 h-24 text-green-500 animate-pulse" />
          </div>

          {/* Title */}
          <h1 className="text-3xl font-black text-orange-500 mb-4 text-center">
            Assessment Complete
          </h1>

          {/* Message */}
          <p className="text-xl text-orange-100 mb-8 text-center leading-relaxed">
            Emergency burn assessment completed successfully following ABA/ATLS protocols.
          </p>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 gap-4 mb-8">
            <div className="bg-black rounded-2xl p-4 border-2 border-orange-500/30">
              <p className="text-orange-300 text-base mb-1 font-bold">Workflow</p>
              <p className="text-orange-100 text-lg font-black">Emergency Protocol</p>
            </div>
            <div className="bg-black rounded-2xl p-4 border-2 border-orange-500/30">
              <p className="text-orange-300 text-base mb-1 font-bold">Status</p>
              <p className="text-green-400 text-lg font-black">✓ Completed</p>
            </div>
          </div>

          {/* Report Generation Buttons */}
          <div className="space-y-4 mb-8">
            <h2 className="text-xl font-black text-orange-400 mb-3">Generate Reports</h2>
            
            <Button
              onClick={handleGeneratePDF}
              disabled={generatingPDF}
              className="w-full h-14 bg-red-600 text-white text-lg font-black rounded-xl hover:bg-red-700 transition-colors flex items-center justify-center gap-3 border-2 border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-6 h-6" />
              {generatingPDF ? 'Generating...' : 'Generate PDF Report'}
            </Button>

            <Button
              onClick={handleGenerateWord}
              disabled={generatingWord}
              className="w-full h-14 bg-blue-600 text-white text-lg font-black rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 border-2 border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-6 h-6" />
              {generatingWord ? 'Generating...' : 'Generate Word Document'}
            </Button>
          </div>

          {/* Navigation Buttons */}
          <div className="space-y-3">
            <Button
              onClick={onMedicalConsultation}
              className="w-full h-14 bg-orange-500 text-white text-lg font-black rounded-xl hover:bg-orange-600 transition-colors flex items-center justify-center gap-3"
            >
              Continue to Medical Consultation
            </Button>

            <Button
              onClick={onBack}
              variant="outline"
              className="w-full h-14 border-2 border-orange-500 bg-transparent text-orange-400 text-lg font-bold rounded-xl hover:bg-orange-500/20 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Mode Selection
            </Button>
          </div>

          {/* Footer Note */}
          <p className="text-zinc-400 text-sm mt-6 text-center leading-relaxed">
            All assessment data has been saved. Reports contain complete question-answer documentation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AssessmentComplete;